/**
 * Simple Content Hub Navigation
 * Lightweight navigation for fixed content sections with Swiper support
 */

class SimpleContentHub {
  constructor() {
    this.navLinks = [];
    this.contentSections = [];
    this.activeSection = null;
    this.isAnimating = false;
    this.swiper = null;
    this.init();
  }

  init() {
    this.bindElements();
    this.initSwiper();
    this.bindEvents();
    this.setInitialActive();
    this.disableHeaderScroll();
    console.log('Simple Content Hub: Initialized');
  }

  disableHeaderScroll() {
    // Disable header scroll behavior on Content Hub page
    if (window.headerScrollController) {
      window.headerScrollController.disable();
      console.log('Simple Content Hub: Header scroll disabled');
    }
    
    // Add body class to ensure header stays visible
    document.body.classList.add('content-hub-page');
    
    // Force header to be visible and add specific class
    const header = document.querySelector('.site-header');
    if (header) {
      header.classList.add('header-always-visible');
      header.classList.remove('header-hidden', 'header-hiding');
    }
  }

  initSwiper() {
    // Initialize Swiper only on mobile/tablet
    if (window.innerWidth <= 1024) {
      const swiperElement = document.querySelector('.content-hub__nav-swiper');
      if (swiperElement && typeof Swiper !== 'undefined') {
        this.swiper = new Swiper('.content-hub__nav-swiper', {
          slidesPerView: 'auto',
          spaceBetween: 0,
          freeMode: true,
          grabCursor: true,
          // No scrollbar, no pagination - clean look
          scrollbar: false,
          pagination: false,
          // Smooth scrolling
          freeMode: {
            enabled: true,
            sticky: false,
            momentumRatio: 0.5,
            momentumBounce: false,
          },
          // Touch settings
          touchEventsTarget: 'container',
          simulateTouch: true,
          allowTouchMove: true,
        });
        console.log('Simple Content Hub: Swiper initialized');
      }
    }
  }

  bindElements() {
    this.container = document.querySelector('[data-content-hub], .content-hub');
    if (!this.container) return;

    this.navLinks = Array.from(this.container.querySelectorAll('[data-content-target]'));
    this.contentSections = Array.from(this.container.querySelectorAll('[data-content-id]'));
    
    console.log(`Simple Content Hub: Found ${this.navLinks.length} nav links and ${this.contentSections.length} content sections`);
  }

  bindEvents() {
    // Navigation click events
    this.navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('data-content-target');
        this.showContent(targetId, link);
      });
    });

    // Keyboard navigation
    this.navLinks.forEach(link => {
      link.addEventListener('keydown', this.handleKeyboardNavigation.bind(this));
    });

    // URL hash navigation
    window.addEventListener('hashchange', this.handleHashChange.bind(this));

    // Resize event to handle Swiper
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  handleResize() {
    // Destroy and recreate Swiper based on screen size
    if (window.innerWidth <= 1024) {
      if (!this.swiper) {
        this.initSwiper();
      }
    } else {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }

  setInitialActive() {
    // Check URL hash first
    const hash = window.location.hash.replace('#', '');
    let initialLink = null;
    let initialSection = null;

    if (hash) {
      // Try to find section by hash
      initialSection = this.contentSections.find(section => 
        section.getAttribute('data-content-id') === hash
      );
      initialLink = this.navLinks.find(link => 
        link.getAttribute('data-content-target') === hash
      );
    }

    // Fallback to first available
    if (!initialLink || !initialSection) {
      initialLink = this.navLinks[0];
      initialSection = this.contentSections[0];
    }

    if (initialLink && initialSection) {
      this.setActiveNavigation(initialLink);
      this.setActiveContent(initialSection, false);
    }
  }

  showContent(targetId, clickedLink = null) {
    if (this.isAnimating) return;

    const targetSection = this.contentSections.find(section => 
      section.getAttribute('data-content-id') === targetId
    );

    const targetLink = clickedLink || this.navLinks.find(link => 
      link.getAttribute('data-content-target') === targetId
    );

    if (!targetSection || !targetLink) {
      console.warn(`Simple Content Hub: Target not found for ID: ${targetId}`);
      return;
    }

    // Don't do anything if already active
    if (targetSection === this.activeSection) return;

    this.isAnimating = true;

    // Update navigation
    this.setActiveNavigation(targetLink);

    // Update content with animation
    this.setActiveContent(targetSection, true);

    // Update URL hash without triggering scroll
    history.replaceState(null, null, `#${targetId}`);

    // Reset animation flag
    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }

  setActiveNavigation(activeLink) {
    // Remove active class from all links
    this.navLinks.forEach(link => {
      link.classList.remove('content-hub__nav-link--active');
      link.setAttribute('aria-pressed', 'false');
    });

    // Add active class to current link
    if (activeLink) {
      activeLink.classList.add('content-hub__nav-link--active');
      activeLink.setAttribute('aria-pressed', 'true');
    }
  }

  setActiveContent(activeSection, animate = true) {
    // Hide all sections
    this.contentSections.forEach(section => {
      section.classList.remove('content-hub__content-section--active');
    });

    // Show active section
    if (activeSection) {
      if (animate) {
        // Add slight delay for smoother transition
        setTimeout(() => {
          activeSection.classList.add('content-hub__content-section--active');
        }, 50);
      } else {
        activeSection.classList.add('content-hub__content-section--active');
      }

      this.activeSection = activeSection;
    }
  }

  handleKeyboardNavigation(e) {
    const currentIndex = this.navLinks.indexOf(e.target);
    let targetIndex = currentIndex;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        targetIndex = (currentIndex + 1) % this.navLinks.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        targetIndex = currentIndex > 0 ? currentIndex - 1 : this.navLinks.length - 1;
        break;
      case 'Home':
        e.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        targetIndex = this.navLinks.length - 1;
        break;
      default:
        return;
    }

    if (targetIndex !== currentIndex) {
      this.navLinks[targetIndex].focus();
      const targetId = this.navLinks[targetIndex].getAttribute('data-content-target');
      this.showContent(targetId, this.navLinks[targetIndex]);
    }
  }

  handleHashChange() {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      this.showContent(hash);
    }
  }

  navigateTo(targetId) {
    this.showContent(targetId);
  }

  getCurrentSection() {
    return this.activeSection;
  }

  refresh() {
    this.bindElements();
    this.setInitialActive();
  }

  // Cleanup when leaving the page
  destroy() {
    // Re-enable header scroll behavior
    if (window.headerScrollController) {
      window.headerScrollController.enable();
      console.log('Simple Content Hub: Header scroll re-enabled');
    }
    
    // Remove body class
    document.body.classList.remove('content-hub-page');
    
    // Remove specific header class
    const header = document.querySelector('.site-header');
    if (header) {
      header.classList.remove('header-always-visible');
    }
    
    // Destroy Swiper if it exists
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.simpleContentHub = new SimpleContentHub();
});

// Cleanup when navigating away from the page
window.addEventListener('beforeunload', () => {
  if (window.simpleContentHub) {
    window.simpleContentHub.destroy();
  }
});

// Handle page visibility changes (for SPA navigation)
document.addEventListener('visibilitychange', () => {
  if (document.hidden && window.simpleContentHub) {
    window.simpleContentHub.destroy();
  }
});

// Expose navigation function globally for external use
window.navigateToContent = (targetId) => {
  if (window.simpleContentHub) {
    window.simpleContentHub.navigateTo(targetId);
  }
};
